import { IconButton } from "@mui/material";
import {
  AUCTION_STATUS_SUCCESS,
  CHECKBOX_SPACE,
  DISABLED_SEARCH_ACTION_SEND_REQUESTS_ATTRIBUTED,
  DISABLED_SEARCH_ACTION_SEND_REQUESTS_CANCELLED,
  DISABLED_SEARCH_ACTION_SEND_REQUESTS_NOT_STARTED,
  REQUEST_CHIP_STATUS_NEW_FILE,
  REQUEST_CHIP_STATUS_NEW_MESSAGE,
  REQUEST_CHIP_STATUS_ONGOING_CHAT,
  REQUEST_CHIP_STATUS_REQUEST_SEEN,
  REQUEST_CHIP_STATUS_SENT_MESSAGE_SEEN,
  SEARCH_ACTION_SEND_REQUEST,
} from "core/consts";
import { getSearchActions } from "core/model/auctions";
import { useGetOntology } from "core/model/utils/ontologies/hooks";
import { isPrintMode } from "core/model/utils/urls";
import { RequestChipStatus } from "core/types";
import { CheckboxInputFieldPresenter } from "ds_legacy/components/CheckboxInputField";
import InfoChip, { InfoChipProps } from "ds_legacy/components/InfoChip";
import Tooltip from "ds_legacy/components/Tooltip";
import { GREY_500, UNAVAILABLE_GREY } from "ds_legacy/materials/colors";
import { HorizontalLayout } from "ds_legacy/materials/layouts";
import { dp } from "ds_legacy/materials/metrics";
import { FONT_SIZE_16 } from "ds_legacy/materials/typography";
import { usePotentialReceiverContext } from "dsl/ecosystems/PatientSearchMerge/Components/SearchPageTables/Tables/PotentialReceiverTable/PotentialReceiverContext";
import { getCheckboxSettings } from "dsl/ecosystems/PatientSearchMerge/Components/SearchPageTables/Tables/shared";
import { useHasOverFlown } from "dsl/hooks";
import { ClipboardPlusIcon, SendIcon } from "lucide-react";
import { useState } from "react";
import { useTranslations } from "translations";
import { CellProps, DefaultTextCell } from "..";
import { DefaultCellBodyText } from "../../styles";
import {
  CONFIRM_FAX_GDPR,
  CONFIRM_SEND,
  ContactAnywayConfirmationModal,
  ContactAnywayModals,
} from "./ConfirmSendModals";

// Search Tables specific column types
export const COLUMN_TYPE_SEARCH_TABLE_PREFERRED_INPUT =
  "preferred_input" as const;
export const COLUMN_TYPE_SEARCH_TABLE_PREFERRED_INPUT_POTENTIAL_RECEIVERS =
  "preferred_input_potential_receivers" as const;
export const COLUMN_TYPE_SEARCH_TABLE_NAME_WITH_ICONS =
  "name_with_icons" as const;
export const COLUMN_TYPE_SEARCH_TABLE_STATUS = "status" as const;
export const COLUMN_TYPE_SEARCH_TABLE_CHECKBOX = "checkbox" as const;
export const COLUMN_TYPE_SEARCH_TABLE_INDENT = "indent" as const;
export const COLUMN_TYPE_SEARCH_TABLE_CONTACT_ANYWAY = "contact_anway" as const;

type ChipColorConfig = {
  color: InfoChipProps["color"];
  variant: InfoChipProps["variant"];
};

function chipColorConfig(value: RequestChipStatus): ChipColorConfig {
  const defaultColor: ChipColorConfig = {
    color: "info",
    variant: "outlined",
  };

  switch (value) {
    case REQUEST_CHIP_STATUS_SENT_MESSAGE_SEEN:
    case REQUEST_CHIP_STATUS_REQUEST_SEEN:
    case REQUEST_CHIP_STATUS_ONGOING_CHAT:
      return {
        color: "success",
        variant: "outlined",
      };
    case REQUEST_CHIP_STATUS_NEW_MESSAGE:
    case REQUEST_CHIP_STATUS_NEW_FILE:
      return {
        color: "info",
        variant: "filled",
      };
    default:
      return defaultColor;
  }
}

export function ProviderNameWithIcons({
  auctionStatus,
  name,
  patient_data_shared,
  shouldRedactHouseNumber,
  testId,
}: CellProps<typeof COLUMN_TYPE_SEARCH_TABLE_NAME_WITH_ICONS>) {
  const translations = useTranslations();
  const isPrint = isPrintMode();

  if (isPrint) return <DefaultCellBodyText>{name}</DefaultCellBodyText>;

  const isAuctionAccepted = auctionStatus === AUCTION_STATUS_SUCCESS;

  return (
    <HorizontalLayout
      style={{
        alignItems: "center",
        gap: dp(4),
        justifyContent: "space-between",
        width: "100%",
      }}
    >
      <DefaultTextCell message={name} testId={testId} />
      {patient_data_shared && (
        <div>
          <Tooltip
            title={
              shouldRedactHouseNumber && !isAuctionAccepted
                ? translations.search.accessToPatientDataGrantedTooltipRehab
                : translations.search.accessToPatientDataGrantedTooltip
            }
          >
            <ClipboardPlusIcon
              data-testid="patient-shared-icon"
              size={FONT_SIZE_16}
              style={{
                color: GREY_500,
                fontSize: FONT_SIZE_16,
              }}
            />
          </Tooltip>
        </div>
      )}
    </HorizontalLayout>
  );
}

export function StatusValue({
  value,
}: CellProps<typeof COLUMN_TYPE_SEARCH_TABLE_STATUS>) {
  const getOntology = useGetOntology();
  const { color, variant } = chipColorConfig(value);
  const message = getOntology({
    key: value ?? 0,
    type: "searchTableStatusChips",
  });

  const { hasOverFlown, ref } = useHasOverFlown<HTMLDivElement>({
    direction: "x",
    value: message,
  });

  return (
    <InfoChip
      color={color}
      label={
        <div ref={ref} className="overflow-hidden text-ellipsis">
          {message}
        </div>
      }
      tooltip={hasOverFlown ? message : ""}
      variant={variant}
    />
  );
}

export function SelectProviderCheckbox({
  disabled,
  value,
}: CellProps<typeof COLUMN_TYPE_SEARCH_TABLE_CHECKBOX>) {
  const {
    getRequestState,
    requestListState: { automaticSearch },
  } = usePotentialReceiverContext();

  const checked =
    !!getRequestState(value.id)?.manually_selected || automaticSearch;

  return (
    <div
      style={{ textOverflow: "" }}
      onClick={(e) => {
        // the row is the clickable element not the checkbox in this case
        e.preventDefault();
      }}
    >
      <CheckboxInputFieldPresenter
        checkboxSpace={CHECKBOX_SPACE.NONE}
        disabled={disabled}
        elementName={`careprovider-${value.careprovider_id}`}
        label=""
        value={checked}
      />
    </div>
  );
}

export function AddColumnIndent() {
  return <div style={{ textOverflow: "" }} />;
}

export function ContactAnywayAction({
  auction,
  careprovider,
  recommendationId,
  refresh,
  sendFax,
  solutionFilterReasons,
}: CellProps<typeof COLUMN_TYPE_SEARCH_TABLE_CONTACT_ANYWAY>) {
  const [modalOpen, setModalOpen] = useState<ContactAnywayModals | null>(null);
  const translations = useTranslations();

  const { [SEARCH_ACTION_SEND_REQUEST]: canSendRequest } =
    getSearchActions(auction);

  if (!canSendRequest) return null;
  if (!recommendationId) return null;

  const { disableRow } = getCheckboxSettings({ auction });

  const tooltip = (() => {
    switch (canSendRequest?.context?.disabled) {
      case DISABLED_SEARCH_ACTION_SEND_REQUESTS_ATTRIBUTED:
        return translations.search.sendRequestCta.contactAnywaySearchWon;
      case DISABLED_SEARCH_ACTION_SEND_REQUESTS_CANCELLED:
        return translations.search.sendRequestCta.contactAnywaySearchStopped;
      case DISABLED_SEARCH_ACTION_SEND_REQUESTS_NOT_STARTED:
        return translations.search.sendRequestCta.contactAnywaySearchNotStarted;
      default:
        return translations.search.sendRequestCta.contactAnywaySearchRunning;
    }
  })();

  return (
    <>
      <Tooltip placement="bottom-start" title={tooltip}>
        <div>
          <IconButton
            aria-disabled={disableRow}
            aria-label={tooltip}
            color="default"
            data-testid={`contact-anyway-${careprovider.id}`}
            disabled={disableRow}
            size="medium"
            sx={{
              "&:disabled": { color: UNAVAILABLE_GREY },
            }}
            onClick={() => {
              if (sendFax) {
                setModalOpen(CONFIRM_FAX_GDPR);
              } else {
                setModalOpen(CONFIRM_SEND);
              }
            }}
          >
            <SendIcon size={FONT_SIZE_16} />
          </IconButton>
        </div>
      </Tooltip>
      <ContactAnywayConfirmationModal
        auction={auction}
        careprovider={careprovider}
        modalOpen={modalOpen}
        recommendationId={recommendationId}
        refresh={refresh}
        setModalOpen={setModalOpen}
        shouldSendFax={sendFax}
        solutionFilterReasons={solutionFilterReasons}
      />
    </>
  );
}
